import { Box, CircularProgress, TablePagination } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  type ColumnDef,
  type RowData,
  type SortingState,
  useTable,
} from "@tanstack/react-table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { BodyCell, type CellPlacement, HeaderCell } from "./cells";
import { filterRows, sortRows } from "./clientRows";
import { getCellValue, withColumnDefaults } from "./columnValues";
import { exportCsv, printTable } from "./exportTable";
import FilterChips from "./FilterChips";
import { features } from "./features";
import { isActiveFilterItem } from "./filterOperators";
import { computeWidths, MAX_AUTOFIT_WIDTH, minWidthOf } from "./layout";
import DataTableToolbar, { type ToolbarPanel } from "./Toolbar";
import type {
  ColumnPinning,
  ColumnVisibilityModel,
  DataTableColDef,
  Density,
  FilterModel,
  PaginationModel,
  SortModel,
} from "./types";
import useTableLayout from "./useTableLayout";

const DENSITY_FACTOR: Record<Density, number> = {
  compact: 0.7,
  standard: 1,
  comfortable: 1.3,
};
const CELL_PADDING = 20;

export interface DataTableProps<R extends RowData> {
  rows: R[];
  columns: DataTableColDef<R>[];
  getRowId: (row: R) => string;
  // Server mode only; client mode counts the filtered rows itself.
  rowCount?: number;
  // Sort, filter and page the rows in the browser instead of asking the parent.
  clientSide?: boolean;
  loading?: boolean;
  rowHeight?: number;
  headerHeight?: number;
  rowSpacing?: number;
  getRowClassName?: (row: R) => string;
  // Leave sort and filter unset and the table keeps them itself.
  sortModel?: SortModel;
  initialSortModel?: SortModel;
  onSortModelChange?: (model: SortModel) => void;
  filterModel?: FilterModel;
  onFilterModelChange?: (model: FilterModel) => void;
  initialPageSize?: number;
  // Set when the page lives in the parent (e.g. it resets on sort or filter).
  paginationModel?: PaginationModel;
  pageSizeOptions?: number[];
  onPaginationModelChange?: (model: PaginationModel) => void;
  initialColumnVisibility?: ColumnVisibilityModel;
  columnVisibilityOverrides?: ColumnVisibilityModel;
  // Set both to hold visibility or widths in the parent, e.g. to share them.
  columnVisibilityModel?: ColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: ColumnVisibilityModel) => void;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  hideHeader?: boolean;
  initialPinning?: Partial<ColumnPinning>;
  persistKey?: string;
  noRowsOverlay?: React.ReactNode;
  showToolbar?: boolean;
  // Replaces the built-in CSV export, e.g. to fetch every server page.
  onExportCsv?: (columns: DataTableColDef<R>[]) => void;
  exporting?: boolean;
  exportFileName?: string;
  // Extra toolbar buttons, before the built-in ones.
  toolbarActions?: React.ReactNode;
  // Sits left of the rows-per-page control.
  footer?: React.ReactNode;
  // Shows every row and drops the footer.
  disablePagination?: boolean;
  // The parent owns which rows are open, so any cell can toggle them.
  expandedRowIds?: string[];
  renderDetailPanel?: (row: R) => React.ReactNode;
  sx?: SxProps<Theme>;
}

function toSorting(model: SortModel): SortingState {
  return model.map(({ field, sort }) => ({ id: field, desc: sort === "desc" }));
}

function fromSorting(sorting: SortingState): SortModel {
  return sorting.map(({ id, desc }) => ({
    field: id,
    sort: desc ? "desc" : "asc",
  }));
}

export default function DataTable<R extends RowData>({
  rows,
  columns: columnDefs,
  getRowId,
  rowCount: serverRowCount,
  clientSide = false,
  loading = false,
  rowHeight = 72,
  headerHeight = 56,
  rowSpacing = 16,
  getRowClassName,
  sortModel,
  initialSortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  initialPageSize = 10,
  paginationModel,
  pageSizeOptions = [10, 20, 40],
  onPaginationModelChange,
  initialColumnVisibility,
  columnVisibilityOverrides,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnWidths,
  onColumnWidthsChange,
  hideHeader = false,
  initialPinning,
  persistKey,
  noRowsOverlay,
  showToolbar = true,
  onExportCsv,
  exporting = false,
  exportFileName,
  toolbarActions,
  footer,
  disablePagination = false,
  expandedRowIds,
  renderDetailPanel,
  sx,
}: DataTableProps<R>) {
  const columns = useMemo(
    () => columnDefs.map(withColumnDefaults),
    [columnDefs],
  );
  const [ownSort, setOwnSort] = useState<SortModel>(initialSortModel ?? []);
  const [ownFilter, setOwnFilter] = useState<FilterModel>({ items: [] });
  const activeSort = sortModel ?? ownSort;
  const activeFilter = filterModel ?? ownFilter;
  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    columnPinning,
    setColumnPinning,
    density,
    setDensity,
    resetLayout,
  } = useTableLayout({
    persistKey,
    initialColumnVisibility,
    columnVisibilityOverrides,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    columnWidths,
    onColumnWidthsChange,
    initialPinning,
    filterModel: activeFilter,
    sortModel: activeSort,
  });
  const [ownPagination, setPagination] = useState<PaginationModel>({
    page: 0,
    pageSize: initialPageSize,
  });
  const pagination = paginationModel ?? ownPagination;
  const [openPanel, setOpenPanel] = useState<ToolbarPanel | null>(null);
  const [filterFocusField, setFilterFocusField] = useState<string>();
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollEdges, setScrollEdges] = useState({ start: true, end: true });
  const scrollRef = useRef<HTMLDivElement>(null);

  const updateScrollEdges = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const maxScroll = element.scrollWidth - element.clientWidth;
    setScrollEdges({
      start: element.scrollLeft <= 0,
      end: element.scrollLeft >= maxScroll - 1,
    });
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(Math.floor(entry.contentRect.width));
      updateScrollEdges();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [updateScrollEdges]);

  const columnsByField = useMemo(
    () => new Map(columns.map((column) => [column.field, column])),
    [columns],
  );

  const widths = computeWidths(
    columns.filter((column) => columnVisibility[column.field] !== false),
    containerWidth,
    columnSizing,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: widths is rebuilt each render; its JSON is the stable key
  const tableColumns = useMemo<Array<ColumnDef<typeof features, R, unknown>>>(
    () =>
      columns.map((column) => ({
        id: column.field,
        accessorFn: (row: R) => getCellValue(column, row),
        header: column.headerName ?? column.field,
        size: widths[column.field] ?? minWidthOf(column),
        minSize: minWidthOf(column),
        enableSorting: column.sortable !== false,
        enableHiding: column.hideable !== false,
      })),
    [columns, JSON.stringify(widths)],
  );

  const sorting = useMemo(() => toSorting(activeSort), [activeSort]);

  const processedRows = useMemo(
    () =>
      clientSide
        ? sortRows(filterRows(rows, columns, activeFilter), columns, activeSort)
        : rows,
    [clientSide, rows, columns, activeFilter, activeSort],
  );
  const rowCount = clientSide
    ? processedRows.length
    : (serverRowCount ?? rows.length);
  const lastPage = Math.max(0, Math.ceil(rowCount / pagination.pageSize) - 1);
  const currentPage = clientSide
    ? Math.min(pagination.page, lastPage)
    : pagination.page;
  const pageRows =
    clientSide && !disablePagination
      ? processedRows.slice(
          currentPage * pagination.pageSize,
          (currentPage + 1) * pagination.pageSize,
        )
      : processedRows;

  const table = useTable({
    features,
    columns: tableColumns,
    data: pageRows,
    getRowId: (row: R) => getRowId(row),
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      columnSizing,
      columnPinning,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      changeSort(fromSorting(next));
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnPinningChange: setColumnPinning,
    manualSorting: true,
    enableMultiSort: true,
    enableSortingRemoval: true,
    sortDescFirst: false,
    isMultiSortEvent: (event: unknown) => {
      const e = event as React.MouseEvent;
      return e.shiftKey || e.ctrlKey || e.metaKey;
    },
    columnResizeMode: "onChange",
  });

  const changePage = useCallback(
    (next: PaginationModel) => {
      setPagination(next);
      onPaginationModelChange?.(next);
    },
    [onPaginationModelChange],
  );

  const changeSort = (model: SortModel) => {
    setOwnSort(model);
    onSortModelChange?.(model);
  };

  const changeFilter = (model: FilterModel) => {
    setOwnFilter(model);
    if (clientSide) changePage({ page: 0, pageSize: pagination.pageSize });
    onFilterModelChange?.(model);
  };

  const moveColumn = (sourceField: string, targetField: string) => {
    if (sourceField === targetField) return;
    const order = table.getAllLeafColumns().map((column) => column.id);
    order.splice(order.indexOf(sourceField), 1);
    order.splice(order.indexOf(targetField), 0, sourceField);
    setColumnOrder(order);
  };

  const autoFit = (field: string) => {
    const column = columnsByField.get(field);
    const grid = scrollRef.current;
    if (!column || !grid) return;
    let widest = 0;
    grid
      .querySelectorAll<HTMLElement>(
        `.DataTable-cell[data-field="${CSS.escape(field)}"] .DataTable-cellContent`,
      )
      .forEach((content) => {
        const inner = content.firstElementChild as HTMLElement | null;
        widest = Math.max(
          widest,
          content.scrollWidth,
          inner ? inner.scrollWidth : 0,
        );
      });
    const width = Math.min(
      Math.max(widest + CELL_PADDING, minWidthOf(column)),
      MAX_AUTOFIT_WIDTH,
    );
    setColumnSizing((current) => ({ ...current, [field]: width }));
  };

  const openFilterPanel = (field?: string) => {
    setFilterFocusField(field);
    setOpenPanel("filters");
  };

  const activeFilterFields = new Set(
    activeFilter.items
      .filter((item) => {
        const column = columnsByField.get(item.field);
        return column && isActiveFilterItem(item, column);
      })
      .map((item) => item.field),
  );

  const densityFactor = DENSITY_FACTOR[density];
  const bodyRowHeight = Math.round(rowHeight * densityFactor);
  const headRowHeight = Math.round(headerHeight * densityFactor);
  const tableRows = table.getRowModel().rows;
  const orderedColumns = [
    ...table.getStartVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getEndVisibleLeafColumns(),
  ];
  const totalWidth = orderedColumns.reduce(
    (sum, column) => sum + column.getSize(),
    0,
  );
  const lastStartId = table.getStartVisibleLeafColumns().at(-1)?.id;
  const firstEndId = table.getEndVisibleLeafColumns()[0]?.id;

  const placementFor = (columnId: string, index: number): CellPlacement => {
    const placement: CellPlacement = {};
    if (index === 0) placement.edge = "first";
    else if (index === orderedColumns.length - 1) placement.edge = "last";
    if (columnId === lastStartId && !scrollEdges.start) {
      placement.shadow = "start";
    }
    if (columnId === firstEndId && !scrollEdges.end) placement.shadow = "end";
    return placement;
  };

  const headers = [
    ...(table.getStartHeaderGroups()[0]?.headers ?? []),
    ...(table.getCenterHeaderGroups()[0]?.headers ?? []),
    ...(table.getEndHeaderGroups()[0]?.headers ?? []),
  ];

  const exportColumns = () => {
    const visible = orderedColumns
      .map((column) => columnsByField.get(column.id))
      .filter(
        (column): column is DataTableColDef<R> =>
          column !== undefined && !column.disableExport,
      );
    return visible.length > 0
      ? visible
      : columns.filter((column) => !column.disableExport);
  };

  return (
    <Box
      className="DataTable-root"
      sx={[
        // Page wrappers set a display font; pin the theme font like MUI controls do.
        (theme) => ({
          ...theme.typography.body2,
          fontSize: "1rem",
          lineHeight: "20px",
          color: "#475467",
          width: "100%",
          minWidth: 0,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showToolbar && (
        <DataTableToolbar
          columns={columns}
          table={table}
          openPanel={openPanel}
          onOpenPanel={setOpenPanel}
          filterModel={activeFilter}
          onFilterModelChange={changeFilter}
          filterFocusField={filterFocusField}
          activeFilterCount={activeFilterFields.size}
          density={density}
          onDensityChange={setDensity}
          onExportCsv={() =>
            onExportCsv
              ? onExportCsv(exportColumns())
              : exportCsv(exportColumns(), processedRows, exportFileName)
          }
          exporting={exporting}
          actions={toolbarActions}
          onPrint={() => printTable(exportColumns(), processedRows)}
          onResetLayout={resetLayout}
        />
      )}
      <FilterChips
        columns={columns}
        filterModel={activeFilter}
        onFilterModelChange={changeFilter}
        onEdit={openFilterPanel}
      />
      <Box
        ref={scrollRef}
        onScroll={updateScrollEdges}
        sx={{ width: "100%", overflowX: "auto", paddingBottom: "4px" }}
      >
        <Box
          role="grid"
          aria-rowcount={rowCount}
          sx={{ width: Math.max(totalWidth, containerWidth) }}
        >
          <Box
            role="row"
            className="DataTable-columnHeaders"
            sx={{
              display: hideHeader ? "none" : "flex",
              minHeight: headRowHeight,
              backgroundColor: "#F2F4F7",
              borderRadius: "12px",
              marginBottom: "8px",
              color: "#303030",
            }}
          >
            {headers.map((header, index) => {
              const column = columnsByField.get(header.column.id);
              if (!column) return null;
              return (
                <HeaderCell
                  key={header.id}
                  header={header}
                  column={column}
                  placement={placementFor(header.column.id, index)}
                  multiSort={sorting.length > 1}
                  filtered={activeFilterFields.has(column.field)}
                  dragging={draggedField === column.field}
                  canDrop={draggedField !== null}
                  onOpenFilter={openFilterPanel}
                  onManageColumns={() => setOpenPanel("columns")}
                  onAutoFit={autoFit}
                  onDragStart={setDraggedField}
                  onDrop={(field) => {
                    if (draggedField) moveColumn(draggedField, field);
                    setDraggedField(null);
                  }}
                  onDragEnd={() => setDraggedField(null)}
                />
              );
            })}
          </Box>
          <Box
            sx={{ position: "relative", minHeight: tableRows.length ? 0 : 300 }}
          >
            {tableRows.map((row, rowIndex) => {
              const cells = [
                ...row.getStartVisibleCells(),
                ...row.getCenterVisibleCells(),
                ...row.getEndVisibleCells(),
              ];
              return (
                <React.Fragment key={row.id}>
                  <Box
                    role="row"
                    data-id={row.id}
                    className={[
                      "DataTable-row",
                      getRowClassName?.(row.original) ?? "",
                    ].join(" ")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: bodyRowHeight,
                      marginTop: rowIndex === 0 ? 0 : `${rowSpacing}px`,
                      backgroundColor: "#FFF",
                      boxShadow: "0px 1px 2px 0px #1018280F",
                      borderRadius: "12px",
                    }}
                  >
                    {cells.map((cell, index) => {
                      const column = columnsByField.get(cell.column.id);
                      if (!column) return null;
                      return (
                        <BodyCell
                          key={cell.id}
                          cell={cell}
                          column={column}
                          rowId={row.id}
                          row={row.original}
                          placement={placementFor(cell.column.id, index)}
                        />
                      );
                    })}
                  </Box>
                  {renderDetailPanel && expandedRowIds?.includes(row.id) && (
                    <Box
                      className="DataTable-detailPanel"
                      sx={{
                        position: "sticky",
                        left: 0,
                        width: containerWidth || "100%",
                        marginTop: "4px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0px 1px 2px 0px #1018280F",
                      }}
                    >
                      {renderDetailPanel(row.original)}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && tableRows.length === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {noRowsOverlay}
              </Box>
            )}
            {loading && (
              <Box
                role="progressbar"
                aria-label="Loading"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.38)",
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {!disablePagination && (
        <Box
          className="DataTable-footer"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: footer ? "space-between" : "flex-end",
            flexWrap: "wrap",
          }}
        >
          {footer}
          <TablePagination
            component="div"
            count={rowCount}
            page={currentPage}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={pageSizeOptions}
            onPageChange={(_event, page) =>
              changePage({ page, pageSize: pagination.pageSize })
            }
            onRowsPerPageChange={(event) =>
              changePage({ page: 0, pageSize: Number(event.target.value) })
            }
          />
        </Box>
      )}
    </Box>
  );
}
